import type { ComputedRef, ModelRef, Ref } from 'vue';
import { isUndefined } from 'lodash-es';
import {
  computed,
  ref,
} from 'vue';

import type { IChangeManagerFormPayload } from '@modules/affiliates-approved/types';
import type { IApiFormErrors } from '@modules/core/services/query/types';
import type {
  FormResult,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import { useAffiliatesTranslate } from '@modules/affiliates-approved/composables';
import { useChangeManagerMutation } from '@modules/affiliates-approved/mutations';
import { useAffiliateManagersQuery } from '@modules/affiliates-approved/queries';
import { ManagerField } from '@modules/affiliates-approved/types';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { EFormInputTypes } from '@modules/forms/enums';

import type { IChangeManagerDialogEmits, IChangeManagerDialogProps } from '../types';

export interface ChangeManagerComposable {
  formValues: Ref<IChangeManagerFormPayload | undefined>;
  isChangingManagerLoading: Ref<false> | Ref<true>;
  errors: Ref<IApiFormErrors | undefined>;
  formSchema: ComputedRef<FormSchema<IChangeManagerFormPayload>>;
  clearError(key: string): void;
  handleChange(
    key: keyof IChangeManagerFormPayload,
    value: IChangeManagerFormPayload[keyof IChangeManagerFormPayload] | undefined,
    result: FormResult<IChangeManagerFormPayload>,
  ): void;
  confirmChanging(event: MouseEvent): void;
  handleShowDialog(): void;
  resetFormValues(): void;
}

export default function useChangeManager(
  props: IChangeManagerDialogProps,
  showDialog: ModelRef<boolean | undefined>,
  emit: IChangeManagerDialogEmits,
): ChangeManagerComposable {
  const { translate } = useAffiliatesTranslate();
  const { confirmEdit } = useConfirmDialogs();
  const formValues = ref<IChangeManagerFormPayload>();

  const {
    data: managersList,
    isLoading: isManagersLoading,
    refetch: getManagers,
  } = useAffiliateManagersQuery();

  const {
    isLoading: isChangingManagerLoading,
    errors,
    clearError,
    mutateAsync: changeAffiliateManager,
  } = useChangeManagerMutation();

  const listOfManagers = computed<IFormSelectInputOption<number>[]>(() => (
    managersList.value?.map(({ id, name }) => ({ value: id, label: name })) ?? []
  ));

  const formSchema = computed<FormSchema<IChangeManagerFormPayload>>(() => ({
    [ManagerField]: {
      type: EFormInputTypes.SELECT,
      label: translate('CHOOSE_MANAGER_LABEL'),
      options: listOfManagers.value,
      loading: isManagersLoading.value,
      disabled: isChangingManagerLoading.value,
      required: true,
    },
  }));

  function handleChange(
    key: keyof IChangeManagerFormPayload,
    value: IChangeManagerFormPayload[keyof IChangeManagerFormPayload] | undefined,
    result: FormResult<IChangeManagerFormPayload>,
  ): void {
    formValues.value = result.values;
  }

  async function applyChangingManager(): Promise<void> {
    if (!props.tableState || !props.selectedAffiliates || isUndefined(formValues.value))
      return;

    await changeAffiliateManager({
      manager_id: formValues.value[ManagerField],
      affiliate_ids: props.selectedAffiliates,
      segment_id: props.tableState.filter_set_id,
      page: props.tableState.page || 1,
    });

    showDialog.value = false;
  }

  function confirmChanging(event: MouseEvent): void {
    if (isUndefined(formValues.value))
      return;

    confirmEdit({
      event,
      message: translate('CONFIRM_QUESTION'),
      acceptLabel: translate('CONFIRM_OK'),
      rejectLabel: translate('CONFIRM_CANCEL'),
      accept: () => {
        void applyChangingManager();
      },
    });
  }

  function handleShowDialog(): void {
    void getManagers();
  }

  function resetFormValues(): void {
    formValues.value = undefined;
    emit('hide');
  }

  return {
    formValues,
    isChangingManagerLoading,
    errors,
    formSchema,
    clearError,
    handleChange,
    confirmChanging,
    handleShowDialog,
    resetFormValues,
  };
}
